import React from 'react';
import isEqual from 'react-fast-compare';

import {MinimalFolder} from 'constants/FolderConstants';
import * as FileSelectorConstants from 'file-browser/FileSelectorConstants';
import makeCancelable, {CancelablePromise} from 'lib/makeCancelable';
import notifications from 'notifications';

import * as FileSelectorUtil from './FileSelectorUtil';
import FolderDAO from './FolderDAO';
import FolderDAOCache from './FolderDAOCache';

interface GetFoldersWrapperProps {
  children: (folders: Array<MinimalFolder> | null) => JSX.Element;
  folderFilter: FileSelectorConstants.FileSelectorFolderFilter;
}

interface GetFoldersWrapperState {
  folders: Array<MinimalFolder> | null;
}

/**
 * A composable wrapper component for providing a filtered set of folders to the wrapped component, e.g.:
 *
 *   <GetFoldersWrapper folderFilter={{showInventory: true}}>
 *     {(folders) => <span>{folders ? `${folders.length} folders` : 'Loading...'}</span>}}
 *   </GetFoldersWrapper>
 *
 */
export default class GetFoldersWrapper extends React.Component<
  GetFoldersWrapperProps,
  GetFoldersWrapperState
> {
  _fetchPromise?: CancelablePromise<Array<MinimalFolder>>;

  override state: GetFoldersWrapperState = {folders: null};

  override componentDidMount(): void {
    // implements Backbone.Events indirectly through _.extend
    FolderDAOCache.on(FolderDAOCache.FOLDER_CACHE_INVALIDATE, this._handleFoldersInvalidate);
    this._fetchData(this.props.folderFilter);
  }

  override UNSAFE_componentWillReceiveProps(nextProps: GetFoldersWrapperProps): void {
    const oldParams = FileSelectorUtil.folderFilterToListParams(this.props.folderFilter);
    const newParams = FileSelectorUtil.folderFilterToListParams(nextProps.folderFilter);
    if (!isEqual(oldParams, newParams)) {
      this._fetchData(nextProps.folderFilter);
    }
  }

  override componentWillUnmount(): void {
    FolderDAOCache.off(FolderDAOCache.FOLDER_CACHE_INVALIDATE, this._handleFoldersInvalidate);
    if (this._fetchPromise) {
      this._fetchPromise.cancel();
    }
  }

  _handleFoldersInvalidate = (): void => {
    this._fetchData(this.props.folderFilter);
  };

  _fetchData = (folderFilter: FileSelectorConstants.FileSelectorFolderFilter): void => {
    if (this._fetchPromise) {
      this._fetchPromise.cancel();
    }
    this._fetchPromise = makeCancelable(
      Promise.resolve<JQueryPromise<Array<MinimalFolder>>>(
        FolderDAO.fetchAll(FileSelectorUtil.folderFilterToListParams(this.props.folderFilter))
        // Promise.resolve types its return as Promise<Awaited<T>>, which has some odd inference that makes
        // TS think the inner promise could resolve to undefined as well
      ) as Promise<Array<MinimalFolder>>
    );
    this._fetchPromise.promise
      .then((folders) => {
        const predicate = FileSelectorUtil.folderPredicate(folderFilter);
        this.setState({
          folders: folders.filter((folder) => predicate(folder)),
        });
      })
      .catch((response) => {
        if (response.isCanceled) {
          return;
        }
        notifications.notifyError(response);
      });
  };

  override render(): JSX.Element {
    return this.props.children(this.state.folders);
  }
}
